import React from "react";
import { Marker } from "react-google-maps";
/* cspell:disable-next-line */
import memoize from "memoizee";
import { PostMarkerData } from "../../types/EventMap";
import { encodeSvg } from "./getEncodedIconByType";
import { color } from "../../styles/colors";
import { useAuth } from "../../contexts/AuthProvider";
import { kpToFt, kpToMp, kpToSp } from "../AnalysisCharts/BaseChart";
import { gqlType } from "@hifieng/common";

type PropsType = {
  posts: Array<PostMarkerData>;
  zoomLevel: number;
  bounds: gqlType.GeoBoundsInput | null;
};

const FONT_SIZE = 12;
const ICON_PADDING = 5;
const CHARACTER_WIDTH_PX = 7;

const half = (num: number) => num * 0.5; // eslint-disable-line no-magic-numbers

const roundToThousandth = (num: number) => {
  const numberOfFixedDigits = 3;
  return parseFloat(num.toFixed(numberOfFixedDigits));
};

const withinBounds = (
  post: PostMarkerData,
  bounds: gqlType.GeoBoundsInput | null
) => {
  if (bounds) {
    if (
      post.coordinates.latitude > bounds.sw.lat &&
      post.coordinates.latitude < bounds.ne.lat &&
      post.coordinates.longitude > bounds.sw.long &&
      post.coordinates.longitude < bounds.ne.long
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return true; // if no bounds then load maker normally.
  }
};

/* eslint-disable no-magic-numbers */
const getMinimumPostSpacing = (
  zoomLevel: number
): { minimumPostSpacing: number } => {
  // Spacing assumes kilometers(KM) are used as the
  // distance unit for posts. If different units are introduced,
  // consider augmenting the spacing accordingly.
  let minimumPostSpacing = 10;
  // let snapToMarker = 1;

  if (zoomLevel > 15) {
    minimumPostSpacing = 0.2;
  } else if (zoomLevel > 14) {
    minimumPostSpacing = 1;
  } else if (zoomLevel > 12) {
    minimumPostSpacing = 3;
  } else if (zoomLevel > 10) {
    minimumPostSpacing = 5;
  }

  return { minimumPostSpacing };
};

// We want to render the post markers on the map as SVGs, but Google Maps doesn't allow that, so we
// have to render out the component using react-dom/server then encode them as images for Google
// maps to render them. This has the side-effect of forcing us having to hard-code the text styles
const generatePostIcon = memoize(function generatePostIcon(
  post: PostMarkerData,
  imperialFlag: boolean,
  spFlag: boolean,
  ftFlag: boolean
) {
  const postFormated = imperialFlag
    ? kpToMp(post.post, 1).toString()
    : spFlag
    ? kpToSp(post.post).toString()
    : ftFlag
    ? kpToFt(post.post).toString()
    : post.post.toFixed(1).toString();
  const height = 2 * ICON_PADDING + FONT_SIZE;
  const width = 2 * ICON_PADDING + postFormated.length * CHARACTER_WIDTH_PX;

  const encodedIcon = encodeSvg(
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${height}px ${width}px`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        stroke={color.gray}
        strokeWidth="2"
        fill={color.darkMineShaft}
        width={`${width}px`}
        height={`${height}px`}
        opacity="0.75"
        vectorEffect="none-scaling-stroke"
      />
      <text
        stroke={color.white}
        fill={color.white}
        strokeWidth="0.5"
        fontSize={`${FONT_SIZE}px`}
        style={{
          fontFamily: `Inter var, sans-serif`,
          fontFeatureSettings: `"ss02" 1`,
          letterSpacing: `1.1px`
        }}
        fontWeight="300"
        textAnchor="middle"
        x={half(width)}
        y={half(height) + half(FONT_SIZE) - 2}
      >
        {postFormated}
      </text>
    </svg>
  );
  return {
    encodedIcon,
    height,
    width
  };
});
/* eslint-enable */

const PostMarkers = ({ posts, zoomLevel, bounds }: PropsType) => {
  const { minimumPostSpacing } = getMinimumPostSpacing(zoomLevel);

  const lastPostByPipeline: {
    [pipelineId: string]: number;
  } = {};

  const visiblePosts: Array<PostMarkerData> = [];

  posts.forEach(post => {
    const postValue = post.post;

    if (withinBounds(post, bounds)) {
      if (lastPostByPipeline[post.pipelineId] === undefined) {
        //This ensures that a label is added to the first post that is seen in a pipeline
        // not always the first post on a pipeline, could be the middle if that is the first part of the pipeline seen
        lastPostByPipeline[post.pipelineId] = -minimumPostSpacing;
      }

      const distanceToLastVisiblePost = roundToThousandth(
        postValue - lastPostByPipeline[post.pipelineId]
      );

      // Adds to visible if:
      // distance to the last post is >= minimumPostSpacing
      if (distanceToLastVisiblePost >= minimumPostSpacing) {
        if (minimumPostSpacing >= 1) {
          // if zoom < 15 and integer posts wanted
          if (postValue % 1 === 0) {
            // if post is in integer then add
            lastPostByPipeline[post.pipelineId] = postValue;
            visiblePosts.push(post);
            /* eslint-disable no-magic-numbers */
          } else if (distanceToLastVisiblePost > minimumPostSpacing + 0.1) {
            /* eslint-enable no-magic-numbers */
            // if no integer within 0.2km of the last expected int then add non int
            lastPostByPipeline[post.pipelineId] = Math.round(postValue);
            // rounded so that integers are shown unless one does not exist within the next 5.1km
            // if not rounded -> if the first post is not an int then the posts following it won't be int
            visiblePosts.push(post);
          }
        } else {
          // if zoom >= 15 then show posts with 0.2 km between them
          // no integer preference since high accuracy
          lastPostByPipeline[post.pipelineId] = postValue;
          visiblePosts.push(post);
        }
      }
    }
  });

  const { permissions } = useAuth();

  return (
    <>
      {visiblePosts &&
        visiblePosts.map(post => {
          const { encodedIcon, height, width } = generatePostIcon(
            post,
            (permissions.user.imperial ? true : false) ||
              post.pipelineId === "enbridge_line_3" ||
              post.pipelineId === "enbridge_line_4",
            permissions.user.station ? true : false,
            permissions.user.ft ? true : false
          );
          const icon = {
            url: encodedIcon,
            anchor: new window.google.maps.Point(half(width), half(height))
          };

          return (
            <Marker
              key={post.id}
              position={{
                lat: post.coordinates.latitude,
                lng: post.coordinates.longitude
              }}
              icon={icon}
              zIndex={10}
            />
          );
        })}
    </>
  );
};

export default React.memo(PostMarkers);
